import { InputWithLabel } from '@adotel/components'
import { useMutation, useQuery } from '@apollo/client'
import { OperationVariables, ApolloQueryResult } from '@apollo/client'
import {
  Button,
  FormControlLabel,
  Grid,
  IconButton,
  Modal,
  Paper,
  Slide,
  Switch,
  Typography
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { useState } from 'react'
import { toast } from 'react-toastify'

import {
  CREDITOR_QUERY,
  UPDATE_CREDITOR_MUTATION
} from '../../graphql/walletCreditors'
import { formatBalance } from '../../helpers/formatBalance'
import {
  addCommaToPrice,
  isValidCreditLimit,
  removeCommaFromPrice
} from '../../helpers/numbers'
import { getRoleFromStorage } from '../../helpers/userData'
import { getDirection, getTranslate } from '../../localization'
import walletPagesRoleManagement from '../../staticRoleManagement/walletRoleManagement'
import { Currency } from '../../types/currency'
import {
  Creditor,
  CreditorListItem,
  UpdateCreditorInput
} from '../../types/walletCreditor'
import { EditCreditorModalSkeleton } from './index.skeleton'
import useStyles from './styles/index.styles'

type EditCreditorModalProps = {
  creditorID: string
  currency: Currency
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<
    ApolloQueryResult<{
      creditors: CreditorListItem[]
    }>
  >
}

export default function EditCreditorModal({
  creditorID,
  currency,
  isOpen,
  setIsOpen,
  refetch
}: EditCreditorModalProps) {
  const classes = useStyles()

  const canEdit = walletPagesRoleManagement
    .find((item) => item.route === '/adopay/:page')
    ?.sections?.EditCreditor.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  // Reported as the balances being wrong; they were never loaded. See helpers/formatBalance
  // for why zero is deliberately not treated as missing.
  const balance = (value: number | undefined | null): string =>
    formatBalance(value, getTranslate(currency || ''))

  const [input, setInput] = useState<UpdateCreditorInput>({
    creditorID: '',
    active: false,
    canUseCredit: false,
    autoSettelmentEnabled: false,
    maxCredit: 0,
    maxReservable: 0
  })

  const onCloseModal = () => {
    setIsOpen(false)
  }

  const {
    data: creditor,
    loading: loadingForGetCreditor,
    error: creditorError,
    refetch: refetchCreditor
  } = useQuery<{
    creditor: Creditor
  }>(CREDITOR_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      creditorID: creditorID
    },
    onCompleted: (res) => {
      setInput({
        creditorID: res.creditor.creditorID,
        maxCredit: res.creditor.maxCredit,
        maxReservable: res.creditor.macReservable,
        active: res.creditor.active,
        canUseCredit: res.creditor.canUseCredit,
        autoSettelmentEnabled: res.creditor.autoSettelmentEnabled
      })
    },
    onError: () => {
      toast.error(getTranslate('Unable to load creditor. Please retry.'))
    }
  })

  const [updateCreditor, { loading: loadingForUpdateCreditor }] = useMutation(
    UPDATE_CREDITOR_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (response) => {
        toast.success(response.updateCreditor.message)
        onCloseModal()
        refetch()
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  if (loadingForGetCreditor) {
    return <EditCreditorModalSkeleton isOpen={isOpen} onClose={onCloseModal} />
  }

  const creditorLoaded = Boolean(creditor?.creditor?.creditorID)

  return (
    <div className={classes.modalContainer}>
      <Modal dir={getDirection()} open={isOpen} onClose={onCloseModal}>
        <Slide in={isOpen} direction="down">
          <Paper className={classes.paper}>
            <div className={classes.header}>
              <Typography>
                {getTranslate(creditor?.creditor.entity.entityName || '')}
              </Typography>
              <IconButton
                onClick={() => {
                  setIsOpen(false)
                }}>
                <CloseIcon />
              </IconButton>
            </div>
            {!creditorLoaded && (
              <div className={classes.loadError} role="alert">
                <Typography color="error">
                  {getTranslate(
                    creditorError
                      ? 'Unable to load creditor. Please retry.'
                      : 'Creditor details are unavailable. Please retry.'
                  )}
                </Typography>
                <Button color="primary" onClick={() => refetchCreditor()}>
                  {getTranslate('Retry')}
                </Button>
              </div>
            )}
            <div className={classes.walletInfo} hidden={!creditorLoaded}>
              <div className={classes.walletInfoContent}>
                <Typography variant="body1" className={classes.titleColor}>
                  {getTranslate('Wallet Balance')}
                </Typography>
                <Typography>
                  {balance(creditor?.creditor.walletBalance)}
                </Typography>
              </div>
              <div className={classes.walletInfoContent}>
                <Typography variant="body1" className={classes.titleColor}>
                  {getTranslate('Credit Balance')}
                </Typography>
                <Typography>
                  {balance(creditor?.creditor.remainingCreditBalance)}
                </Typography>
              </div>
              <div className={classes.walletInfoContent}>
                <Typography variant="body1" className={classes.titleColor}>
                  {getTranslate('Remaining Reservable')}
                </Typography>
                <Typography>
                  {balance(creditor?.creditor.remainingReservable)}
                </Typography>
              </div>
            </div>
            <div className={classes.amountSection} hidden={!creditorLoaded}>
              <InputWithLabel
                label={getTranslate('Max Credit')}
                placeholder={getTranslate('Enter max credit')}
                autoComplete="off"
                fullWidth
                value={addCommaToPrice(input.maxCredit)}
                onChange={(event) => {
                  const commaLessValue = removeCommaFromPrice(
                    event.currentTarget.value
                  )
                  setInput({
                    ...input,
                    maxCredit: Number(commaLessValue)
                  })
                }}
                inputSize="small"
                disabled={!canEdit}
              />
              <InputWithLabel
                label={getTranslate('Max Reservable')}
                placeholder={getTranslate('Enter max reservable')}
                autoComplete="off"
                fullWidth
                disabled={!canEdit}
                value={addCommaToPrice(input.maxReservable)}
                onChange={(event) => {
                  const commaLessValue = removeCommaFromPrice(
                    event.currentTarget.value
                  )
                  setInput({
                    ...input,
                    maxReservable: Number(commaLessValue)
                  })
                }}
                inputSize="small"
              />
            </div>
            <div className={classes.switches} hidden={!creditorLoaded}>
              <Grid item xs={12} sm={3}>
                <FormControlLabel
                  control={
                    <Switch
                      disabled={!canEdit}
                      checked={input.active}
                      onChange={() =>
                        setInput({
                          ...input,
                          active: !input.active
                        })
                      }
                      color="primary"
                    />
                  }
                  label={getTranslate('Active')}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  className={classes.switch}
                  control={
                    <Switch
                      disabled={!canEdit}
                      checked={input.canUseCredit}
                      onChange={() =>
                        setInput({
                          ...input,
                          canUseCredit: !input.canUseCredit
                        })
                      }
                      color="primary"
                    />
                  }
                  label={getTranslate('Can use credit')}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <FormControlLabel
                  className={classes.switch}
                  control={
                    <Switch
                      disabled={!canEdit}
                      checked={input.autoSettelmentEnabled}
                      onChange={() =>
                        setInput({
                          ...input,
                          autoSettelmentEnabled: !input.autoSettelmentEnabled
                        })
                      }
                      color="primary"
                    />
                  }
                  label={getTranslate('Enable Auto Settlement')}
                />
              </Grid>
            </div>
            <div className={classes.buttons} hidden={!creditorLoaded}>
              <Button variant="text" color="primary" onClick={onCloseModal}>
                {getTranslate('Cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                className={classes.saveButton}
                disabled={
                  // `input` starts empty and is filled by the query's onCompleted,
                  // which never runs if the lookup fails — and only `loading` gates
                  // the form, so a failed load still renders it. Without this check
                  // Save would be offered for creditorID '', because zero is now a
                  // valid limit and the defaults are zero. The old truthiness guard
                  // covered this by accident; it needs saying explicitly.
                  !input.creditorID ||
                  !isValidCreditLimit(input.maxCredit) ||
                  !isValidCreditLimit(input.maxReservable) ||
                  loadingForUpdateCreditor ||
                  !canEdit
                }
                onClick={() => {
                  updateCreditor({
                    variables: {
                      input: input
                    }
                  })
                }}>
                {getTranslate('Save')}
              </Button>
            </div>
          </Paper>
        </Slide>
      </Modal>
    </div>
  )
}
